import React, { useState } from 'react';
import {
  Box,
  Paper,
  Button,
  Drawer,
  Chip,
  Badge,
  Typography,
  IconButton,
  Divider,
  Collapse,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export interface ActiveFilter {
  key: string;
  label: React.ReactNode;
  onRemove?: () => void;
}

export interface FilterPanelProps {
  /** The filter controls (TextFields, Selects, date pickers...). */
  children: React.ReactNode;
  /** Always-visible search slot (rendered outside the collapsible area on desktop). */
  searchSlot?: React.ReactNode;
  /** Chips describing currently-applied filters; each can be individually removed. */
  activeFilters?: ActiveFilter[];
  /** Clear-all handler. Shows a "clear" affordance when provided. */
  onClear?: () => void;
  /** Optional apply handler — renders an apply button (useful for non-live filters). */
  onApply?: () => void;
  applyLabel?: string;
  clearLabel?: string;
  title?: string;
  /** On desktop, start collapsed (toolbar hidden behind a toggle). Default false. */
  defaultCollapsed?: boolean;
}

/**
 * Responsive filter container.
 *
 * - Desktop: a clean toolbar (Paper) with the controls laid out in a wrapping grid,
 *   plus a row of removable active-filter chips.
 * - Mobile: collapses into a single "فیلترها" button (with an active-count badge)
 *   that opens a bottom-sheet drawer holding the same controls + apply/clear.
 *
 * The caller owns all filter state & data fetching; this only handles layout.
 */
const FilterPanel: React.FC<FilterPanelProps> = ({
  children,
  searchSlot,
  activeFilters = [],
  onClear,
  onApply,
  applyLabel = 'اعمال فیلتر',
  clearLabel = 'پاک کردن',
  title = 'فیلترها',
  defaultCollapsed = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expanded, setExpanded] = useState(!defaultCollapsed);
  const activeCount = activeFilters.length;

  const chips = activeCount > 0 && (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
      {activeFilters.map((f) => (
        <Chip
          key={f.key}
          label={f.label}
          size="small"
          onDelete={f.onRemove}
          variant="outlined"
          color="primary"
          sx={{ maxWidth: 220, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
        />
      ))}
      {onClear && (
        <Button size="small" color="inherit" onClick={onClear} sx={{ color: 'text.secondary' }}>
          {clearLabel}
        </Button>
      )}
    </Box>
  );

  // ---- Mobile: trigger button + bottom-sheet drawer ----
  if (isMobile) {
    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {searchSlot && <Box sx={{ flex: 1, minWidth: 0 }}>{searchSlot}</Box>}
          <Badge badgeContent={activeCount} color="primary" overlap="circular">
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setDrawerOpen(true)}
              sx={{ whiteSpace: 'nowrap', minHeight: 42 }}
            >
              {title}
            </Button>
          </Badge>
        </Box>

        {activeCount > 0 && <Box sx={{ mt: 1.5 }}>{chips}</Box>}

        <Drawer
          anchor="bottom"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: { borderRadius: '16px 16px 0 0', maxHeight: '85vh' },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.5,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {title}
              </Typography>
              <IconButton onClick={() => setDrawerOpen(false)} aria-label="بستن" size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Divider />
            <Box sx={{ p: 2, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {children}
            </Box>
            <Divider />
            <Box sx={{ display: 'flex', gap: 1, p: 2 }}>
              {onClear && (
                <Button
                  fullWidth
                  color="inherit"
                  variant="outlined"
                  onClick={() => {
                    onClear();
                  }}
                  sx={{ minHeight: 44 }}
                >
                  {clearLabel}
                </Button>
              )}
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  onApply?.();
                  setDrawerOpen(false);
                }}
                sx={{ minHeight: 44 }}
              >
                {applyLabel}
              </Button>
            </Box>
          </Box>
        </Drawer>
      </Box>
    );
  }

  // ---- Desktop: inline toolbar ----
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: expanded ? 1.5 : 0 }}>
        <FilterListIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {activeCount > 0 && (
          <Chip label={activeCount} size="small" color="primary" sx={{ height: 20, minWidth: 20 }} />
        )}
        <Box sx={{ flex: 1 }} />
        {searchSlot && <Box sx={{ minWidth: 220, maxWidth: 320 }}>{searchSlot}</Box>}
        {defaultCollapsed && (
          <IconButton
            size="small"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? 'بستن فیلترها' : 'باز کردن فیلترها'}
            sx={{
              transition: 'transform 0.2s',
              transform: expanded ? 'rotate(180deg)' : 'none',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        )}
      </Box>

      <Collapse in={expanded} unmountOnExit={false}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 1.5,
            alignItems: 'end',
          }}
        >
          {children}
        </Box>

        {(onApply || onClear) && (
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
            {onClear && (
              <Button color="inherit" onClick={onClear} sx={{ color: 'text.secondary' }}>
                {clearLabel}
              </Button>
            )}
            {onApply && (
              <Button variant="contained" onClick={onApply}>
                {applyLabel}
              </Button>
            )}
          </Box>
        )}
      </Collapse>

      {activeCount > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          {chips}
        </>
      )}
    </Paper>
  );
};

export default FilterPanel;
